/**
 * LeadDetail — header + Details / Activity / Notes tabs. Shared by the
 * drawer and the `/leads/:id` page so both show identical data.
 */
import { useState } from 'react';
import { BellRing, Loader2 } from 'lucide-react';
import { errorMessage, useToast } from '@/components/Toast';
import { useLead, useStages } from '@/features/pipeline/api';
import { fullName } from '@/features/pipeline/types';
import { ApiError } from '@/lib/api-client';
import { usePageTitle } from '@/lib/page-title';
import { daysLabel, fullDateTime, relativeTime } from '@/lib/time';
import { LeadActivityTab } from './LeadActivityTab';
import { LeadDetailsTab } from './LeadDetailsTab';
import { LeadNotesTab } from './LeadNotesTab';
import { LeadScreeningTab } from './LeadScreeningTab';
import { isPromptOpenError, useNudgeLead } from './nudge-api';
import { nudgeStatusLabel } from './nudge-format';

export const PROMPT_OPEN_MESSAGE = 'A nudge is already waiting for a reply';

type Tab = 'details' | 'screening' | 'activity' | 'notes';
const TABS: ReadonlyArray<{ id: Tab; label: string }> = [
  { id: 'details', label: 'Details' },
  { id: 'screening', label: 'AI Screen' },
  { id: 'activity', label: 'Activity' },
  { id: 'notes', label: 'Notes' },
];

export function LeadDetailSkeleton() {
  return (
    <div className="animate-pulse space-y-3 p-4" aria-busy="true" aria-label="Loading candidate">
      <div className="h-5 w-40 rounded bg-gray-200" />
      <div className="h-3 w-56 rounded bg-gray-100" />
      <div className="mt-6 h-10 rounded bg-gray-100" />
      <div className="h-24 rounded bg-gray-100" />
    </div>
  );
}

export function LeadDetail({
  leadId,
  titleId,
  headingLevel = 2,
}: {
  leadId: string;
  titleId?: string;
  /** `/leads/:id` is a page, so the name is its h1; the drawer keeps h2 under the board's h1. */
  headingLevel?: 1 | 2;
}) {
  const [tab, setTab] = useState<Tab>('details');
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  const toast = useToast();
  const { data: lead, isPending, isError, error, refetch } = useLead(leadId);
  const { data: stages = [] } = useStages();
  const nudge = useNudgeLead(leadId);
  const notFound = isError && error instanceof ApiError && error.status === 404;
  // Only the full page names the document; the drawer sits under the board's own title.
  usePageTitle(headingLevel === 1 ? (lead ? fullName(lead.candidate) : notFound ? 'Candidate not found' : 'Candidate') : null);

  if (isPending) return <LeadDetailSkeleton />;
  if (isError || !lead) {
    // The page keeps its one heading even when the lead cannot be shown (Stage 7 QA).
    return (
      <div className="p-4 sm:p-6">
        <Heading id={titleId} className="text-lg font-semibold leading-6 text-gray-900">
          {notFound ? 'Candidate not found' : 'Could not load this candidate'}
        </Heading>
        <p className="mt-1.5 text-sm text-muted">
          {notFound
            ? 'This link may be out of date, or the candidate belongs to another company.'
            : error?.message || 'Something went wrong. Try again.'}
        </p>
        {!notFound && (
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-3 inline-flex min-h-10 items-center rounded-md border border-gray-200 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-page"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  const stage = stages.find((s) => s.id === lead.stage_id);
  const nudgeStatus = nudgeStatusLabel(lead.nudge);
  // Stage 5 QA: nothing to ask about once the lead is hired or lost (the worker answers 409 stage_closed).
  const canNudge = !stage || stage.kind === 'open';
  const onNudge = () =>
    nudge.mutate(undefined, {
      onSuccess: () =>
        toast.push({ kind: 'success', title: 'Nudge sent', message: `The recipients for ${lead.role.title} were asked about ${fullName(lead.candidate)}.` }),
      onError: (err) =>
        isPromptOpenError(err)
          ? toast.push({ kind: 'info', title: PROMPT_OPEN_MESSAGE, message: 'Wait for the reply, or for the question to expire, before nudging again.' })
          : toast.push({ kind: 'error', title: 'Could not send the nudge', message: errorMessage(err) }),
    });
  const onTabKey = (e: React.KeyboardEvent, i: number) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = TABS[(i + step + TABS.length) % TABS.length]!;
    setTab(next.id);
    (e.currentTarget.parentElement?.querySelector<HTMLElement>(`#tab-${next.id}`))?.focus();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-gray-200 px-4 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Heading id={titleId} className="text-lg font-semibold leading-6 text-gray-900">
              {fullName(lead.candidate)}
            </Heading>
            <p className="mt-0.5 text-[13px] text-muted">
              {lead.role.title}
              {stage && (
                <>
                  <span aria-hidden> · </span>
                  {stage.name}
                  {daysLabel(lead.stage_entered_at) && (
                    <span> for {daysLabel(lead.stage_entered_at).replace('today', 'less than a day')}</span>
                  )}
                </>
              )}
            </p>
          </div>
          {canNudge && (
            <button
              type="button"
              onClick={onNudge}
              disabled={nudge.isPending}
              className="inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-800 hover:bg-page disabled:opacity-50"
            >
              {nudge.isPending ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <BellRing size={14} strokeWidth={2} aria-hidden />}
              Nudge now
            </button>
          )}
        </div>
        {nudgeStatus && (
          <p className="mt-1.5 flex items-center gap-1.5 text-[12px] text-muted" data-testid="nudge-status">
            <BellRing size={12} strokeWidth={2} aria-hidden className={nudgeStatus.awaiting ? 'text-warning-text' : undefined} />
            <span>
              Nudged{' '}
              <time dateTime={nudgeStatus.sentAt} title={fullDateTime(nudgeStatus.sentAt)}>{relativeTime(nudgeStatus.sentAt)}</time>
              {nudgeStatus.awaiting && (
                <>
                  <span aria-hidden> · </span>
                  <span className="text-warning-text">{nudgeStatus.awaiting}</span>
                </>
              )}
            </span>
          </p>
        )}
        <div role="tablist" aria-label="Candidate sections" className="-mb-px mt-3 flex gap-1">
          {TABS.map((t, i) => (
            <button
              key={t.id}
              id={`tab-${t.id}`}
              role="tab"
              type="button"
              aria-selected={tab === t.id}
              aria-controls={`panel-${t.id}`}
              tabIndex={tab === t.id ? 0 : -1}
              onClick={() => setTab(t.id)}
              onKeyDown={(e) => onTabKey(e, i)}
              className={`min-h-10 border-b-2 px-3 text-[13px] font-medium ${
                tab === t.id ? 'border-navy text-navy' : 'border-transparent text-muted hover:text-gray-900'
              }`}
            >
              {t.label}
              {t.id === 'notes' && lead.notes.length > 0 && (
                <span className="ml-1 text-[12px] text-muted">{lead.notes.length}</span>
              )}
            </button>
          ))}
        </div>
      </div>
      <div id={`panel-${tab}`} role="tabpanel" aria-labelledby={`tab-${tab}`} className="flex-1 overflow-y-auto p-4 overscroll-contain">
        {tab === 'details' && <LeadDetailsTab lead={lead} stages={stages} onShowScreening={() => setTab('screening')} />}
        {tab === 'screening' && <LeadScreeningTab lead={lead} />}
        {tab === 'activity' && <LeadActivityTab leadId={lead.id} events={lead.events} stages={stages} />}
        {tab === 'notes' && <LeadNotesTab leadId={lead.id} notes={lead.notes} />}
      </div>
    </div>
  );
}
